import { Badge } from "../../../../../ui/components/Badge";
import { Button } from "../../../../../ui/components/Button";
import { Table } from "../../../../../ui/components/Table";
import { Accordion } from "../../../../../ui/components/Accordion";
import type { AccountantStatusInvoicesDetailEntity } from "../../../domain/entities/AccountantStatusEntity";
import { formatIndianAmount } from "../../utils/formatPlAmount";

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return "-";
  return formatIndianAmount(value);
}

interface InvoicesDetailSectionProps {
  data: AccountantStatusInvoicesDetailEntity | null;
  loading: boolean;
  error: string | null;
  offset: number;
  limit: number;
  onPrevious: () => void;
  onNext: () => void;
}

export function InvoicesDetailSection({
  data,
  loading,
  error,
  offset,
  limit,
  onPrevious,
  onNext,
}: InvoicesDetailSectionProps) {
  if (loading) {
    return <div className="h-44 w-full animate-pulse rounded-md bg-neutral-100" />;
  }
  if (error) {
    return (
      <div className="w-full rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
        <span className="text-body font-body text-error-700">{error}</span>
      </div>
    );
  }
  if (!data) return null;

  const total = data.pagination?.total ?? 0;
  const hasMore = data.pagination?.hasMore ?? false;

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background shadow-sm">
      <Accordion
        trigger={
          <div className="flex w-full items-center gap-2 px-6 py-6">
            <div className="flex grow shrink-0 basis-0 items-center gap-4">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Invoices for {data.month} {data.year}
              </span>
            </div>
            <Accordion.Chevron />
          </div>
        }
        defaultOpen={true}
      >
        <div className="flex w-full flex-col items-start gap-4 border-t border-solid border-neutral-border px-6 py-6">
          <div className="flex w-full items-center gap-2">
            <span className="text-body font-body text-default-font">
              Total created: {data.totalCreated}
            </span>
            <span className="text-body font-body text-subtext-color">•</span>
            <span className="text-body font-body text-success-600">
              Processed: {data.processed}
            </span>
            <span className="text-body font-body text-subtext-color">•</span>
            <span className="text-body font-body text-warning-600">
              Pending: {data.pending}
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-2 overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Customer</Table.HeaderCell>
                  <Table.HeaderCell>Invoice Count</Table.HeaderCell>
                  <Table.HeaderCell>Total Amount</Table.HeaderCell>
                  <Table.HeaderCell>Status Summary</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {data.customers.map((customer, idx) => (
                <Table.Row key={`${customer.customerName}-${idx}`}>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {customer.customerName}
                    </span>
                  </Table.Cell>
                  <Table.Cell>{customer.invoiceCount}</Table.Cell>
                  <Table.Cell>{formatCurrency(customer.totalAmount)}</Table.Cell>
                  <Table.Cell>
                    {customer.statusType === "success" ? (
                      <Badge variant="success">{customer.status}</Badge>
                    ) : (
                      <span className="whitespace-nowrap text-body font-body text-warning-600">
                        {customer.status}
                      </span>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          </div>
          {data.pagination && total > 0 && (
            <div className="flex w-full items-center justify-between gap-4 pt-4">
              <span className="text-body font-body text-subtext-color">
                Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} customers
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={offset === 0}
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={!hasMore}
                  onClick={onNext}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </Accordion>
    </div>
  );
}
